import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

export interface VersionRequirement {
  minimum_version: string;
  ios_app_store_url: string | null;
  android_play_store_url: string | null;
  message_title: string;
  message_description: string;
}

export interface VersionCheckResult {
  needsUpdate: boolean;
  requirement: VersionRequirement | null;
  currentVersion: string;
  platform: 'ios' | 'android' | 'web';
}

type CapacitorAppPlugin = {
  getInfo: () => Promise<{ version?: string; versionName?: string; build?: string | number }>;
};

async function loadCapacitorApp(): Promise<CapacitorAppPlugin | null> {
  try {
    const moduleName = '@capacitor/app';
    const appModule = await import(/* @vite-ignore */ moduleName);
    return (appModule as { App?: CapacitorAppPlugin }).App ?? null;
  } catch (error) {
    console.warn('[AppVersionCheck] Capacitor App plugin unavailable:', error);
    return null;
  }
}

/**
 * Compare two version strings (e.g., "1.2.3" vs "1.2.4")
 * Returns: -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
function compareVersions(version1: string, version2: string): number {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);
  
  const maxLength = Math.max(v1Parts.length, v2Parts.length);
  
  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;
    
    if (v1Part < v2Part) return -1;
    if (v1Part > v2Part) return 1;
  }
  
  return 0;
}

/**
 * Get the current app version from the device
 */
async function getCurrentAppVersion(): Promise<string> {
  try {
    console.log('[AppVersionCheck] getCurrentAppVersion - isNativePlatform:', Capacitor.isNativePlatform());
    console.log('[AppVersionCheck] getCurrentAppVersion - platform:', Capacitor.getPlatform());
    
    if (!Capacitor.isNativePlatform()) {
      console.log('[AppVersionCheck] Not on native platform, returning 0.0.0');
      return '0.0.0';
    }
    const appPlugin = await loadCapacitorApp();
    if (!appPlugin) return '0.0.0';

    try {
      console.log('[AppVersionCheck] Calling App.getInfo()...');
      const appInfo = await appPlugin.getInfo();
      console.log('[AppVersionCheck] App.getInfo() result:', JSON.stringify(appInfo, null, 2));
      
      if (appInfo && appInfo.version) {
        console.log('[AppVersionCheck] ✅ Version found:', appInfo.version);
        return appInfo.version;
      } else {
        console.warn('[AppVersionCheck] ⚠️ App.getInfo() returned but version is missing');
        console.warn('[AppVersionCheck] Full appInfo object:', appInfo);
        
        // Try alternative fields (Android uses versionName, iOS uses version)
        if (appInfo) {
          if (appInfo.versionName) {
            console.log('[AppVersionCheck] Using versionName as fallback:', appInfo.versionName);
            return appInfo.versionName;
          }
          if (appInfo.build) {
            console.log('[AppVersionCheck] Using build as fallback:', appInfo.build);
            return String(appInfo.build);
          }
        }
        return '0.0.0';
      }
    } catch (error) {
      console.error('[AppVersionCheck] ❌ Error getting app version:', error);
      console.error('[AppVersionCheck] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined
      });
      return '0.0.0';
    }
  } catch (error) {
    console.error('[AppVersionCheck] Unexpected error in getCurrentAppVersion:', error);
    return '0.0.0';
  }
}

/**
 * Get the platform (ios, android, or web)
 */
function getPlatform(): 'ios' | 'android' | 'web' {
  if (!Capacitor.isNativePlatform()) {
    return 'web';
  }
  
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    return 'ios';
  } else if (platform === 'android') {
    return 'android';
  }
  
  return 'web';
}

/**
 * Fetch minimum version requirement from database
 */
async function fetchVersionRequirement(platform: 'ios' | 'android'): Promise<VersionRequirement | null> {
  try {
    const { data, error } = await supabase
      .rpc('get_minimum_app_version', { p_platform: platform });
    
    if (error) {
      console.error('[AppVersionCheck] Error fetching version requirement:', error);
      return null;
    }
    
    if (!data || data.length === 0) {
      console.log('[AppVersionCheck] No version requirement found for platform:', platform);
      return null;
    }
    
    return data[0] as VersionRequirement;
  } catch (error) {
    console.error('[AppVersionCheck] Exception fetching version requirement:', error);
    return null;
  }
}

/**
 * Check if the current app version meets the minimum requirement
 */
export async function checkAppVersion(): Promise<VersionCheckResult> {
  const platform = getPlatform();
  const currentVersion = await getCurrentAppVersion();
  
  console.log('[AppVersionCheck] Platform:', platform);
  console.log('[AppVersionCheck] Current version:', currentVersion);
  
  // Skip version check for web platform
  if (platform === 'web') {
    console.log('[AppVersionCheck] Web platform detected, skipping version check');
    return {
      needsUpdate: false,
      requirement: null,
      currentVersion,
      platform: 'web'
    };
  }
  
  // Fetch minimum version requirement from database
  const requirement = await fetchVersionRequirement(platform);
  
  if (!requirement) {
    console.log('[AppVersionCheck] No version requirement found, allowing access');
    return {
      needsUpdate: false,
      requirement: null,
      currentVersion,
      platform
    };
  }
  
  console.log('[AppVersionCheck] Minimum required version:', requirement.minimum_version);
  
  // Compare versions
  const comparison = compareVersions(currentVersion, requirement.minimum_version);
  const needsUpdate = comparison < 0;
  
  console.log('[AppVersionCheck] Version comparison:', {
    current: currentVersion,
    minimum: requirement.minimum_version,
    comparison,
    needsUpdate
  });
  
  return {
    needsUpdate,
    requirement,
    currentVersion,
    platform
  };
}

/**
 * Get the appropriate app store URL based on platform
 */
export function getAppStoreUrl(requirement: VersionRequirement, platform: 'ios' | 'android'): string | null {
  if (platform === 'ios') {
    return requirement.ios_app_store_url;
  } else if (platform === 'android') {
    return requirement.android_play_store_url;
  }
  return null;
}

